/**
 * Decorative hero art for the "Book your court" screen
 * (ConnectionErrorScreen.jsx): two courts set on a diagonal, receding towards
 * the upper right with raised nets, the sun on the horizon behind them, and a
 * calendar badge with a winding path and a check mark in the lower left.
 *
 * Every fill and stroke is set by class in ConnectionErrorScreen.css from the
 * Active Button Color (--jt-cyan): court shapes, sun and badge filled with
 * it, every line a lighter (translucent white) version of it — nothing here
 * carries a colour of its own.
 *
 * Geometry is in a 345 x 230 viewBox. Each court is a parallelogram whose
 * long sides run from the far baseline (upper left) to the near one (lower
 * right); kitchen lines are parallel to the baselines, the centre lines run
 * baseline-to-kitchen, and the net is a strip lifted off the court between
 * two posts. The back court runs off the right edge on purpose.
 *
 * @param {object} props
 * @param {string} [props.className] - sizing class for the <svg>.
 * @returns {JSX.Element}
 */
function CourtHorizonArt({ className = '' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 345 230"
      aria-hidden="true"
      focusable="false"
    >
      {/* Sun first, so the ground covers its lower half as it "rises". The
          ground is filled with the card's own colour, so the art has no
          backdrop of its own and sits straight on the card. */}
      <circle className="cha-sun-glow" cx="262" cy="30" r="34" />
      <circle className="cha-sun" cx="262" cy="30" r="22" />
      <path className="cha-ground" d="M0 68 L345 16 L345 230 L0 230 Z" />
      <path className="cha-horizon" d="M0 68 L345 16" />

      {/* Back court (clipped by the right edge) */}
      <g className="cha-court">
        <path className="cha-court-surface" d="M165 62 L238 44 L400 104 L327 122 Z" />
        <path className="cha-court-line" d="M165 62 L238 44 L400 104 L327 122 Z" />
        <path className="cha-court-line" d="M198 77 L271 59 M269 103 L342 85 M201 53 L234 68 M306 91 L364 113" />
        <path className="cha-court-net-band" d="M238 89 L311 71 L311 59 L238 77 Z" />
        <path className="cha-court-net" d="M238 89 V77 L311 59 V71" />
      </g>

      {/* Front court */}
      <g className="cha-court">
        <path className="cha-court-surface" d="M27 92 L120 72 L315 160 L230 205 Z" />
        <path className="cha-court-line" d="M27 92 L120 72 L315 160 L230 205 Z" />
        <path className="cha-court-line" d="M88 126 L178 98 M153 162 L241 127 M73 82 L133 112 M197 144 L272 182" />
        <path className="cha-court-net-band" d="M118 143 L208 112 L208 96 L118 127 Z" />
        <path className="cha-court-net" d="M118 143 V127 L208 96 V112" />
      </g>

      {/* Calendar badge, on its own below the front court. */}
      <g className="cha-calendar">
        <rect className="cha-calendar-body" x="48" y="166" width="58" height="54" rx="9" />
        <path className="cha-calendar-bar" d="M48 182 H106" />
        <path className="cha-calendar-ring" d="M62 160 V172 M92 160 V172" />
        <path className="cha-calendar-path" d="M54 214 C66 210 60 198 72 196 S86 190 92 188" />
        <circle className="cha-calendar-check-bg" cx="104" cy="214" r="12" />
        <path className="cha-calendar-check" d="M98.5 214.5 L102.5 218.5 L109.5 210" />
      </g>
    </svg>
  );
}

export default CourtHorizonArt;
